import { useAtomValue } from "jotai";
import styles from "./registrationForm.module.scss";
import { ReactComponent as Logo } from "../../assets/James-Hope-University-Logo.svg";
import { userAtom } from "../../utils/store";
import { formatFullDate } from "../../utils/formatDate";
import { isValidDate } from "../../utils";
import { formatCurrency } from "../../utils/currency";

const isBscApplicant = (programType?: string) =>
  programType?.trim().toLowerCase().includes("bsc");

const isDirectEntryApplicant = (modeOfEntry?: string) =>
  modeOfEntry?.trim().toLowerCase().replace(/\s+/g, "").includes("directentry");

const isUtmeApplicant = (modeOfEntry?: string) =>
  modeOfEntry?.trim().toLowerCase().replace(/\s+/g, "").includes("utme");

interface RegistrationFormProps {
  personalInfo: PersonalInfo;
  institutions?: InstitutionData[];
  qualifications?: QualificationData[];
  jambResult?: JambResultData;
  workHistory?: WorkHistoryData[];
  nyscUpload: NyscUpload;
  printRef: React.RefObject<HTMLDivElement>;
  shouldPayLateApplicationFee: boolean;
  lateApplicationFee: number;
  discountAmount: number;
  totalAmount: number;
}

const documentName = (str?: string | null) =>
  str ? str.slice(str.lastIndexOf("/") + 1) : "No file uploaded";

const RegistrationForm = ({
  personalInfo,
  institutions,
  workHistory,
  nyscUpload,
  qualifications,
  jambResult,
  printRef,
  shouldPayLateApplicationFee,
  lateApplicationFee,
  discountAmount,
  totalAmount,
}: RegistrationFormProps) => {
  const user = useAtomValue(userAtom);
  const shouldShowInstitutionSection =
    user?.programType?.toLowerCase()?.includes("msc") ||
    (isBscApplicant(user?.programType) && isDirectEntryApplicant(user?.modeOfEntry));
  const shouldShowUtmeSection =
    isUtmeApplicant(user?.modeOfEntry) && !!jambResult?.examNumber;

  return (
    <div className={styles.formWrapper} ref={printRef}>
      <div className={styles.formHeader}>
        <div className={styles.headerContent}>
          <div className={styles.logoSection}>
            <Logo className={styles.logo} />
          </div>
          <div className={styles.headerText}>
            <h1>JAMES HOPE UNIVERSITY</h1>
            <h2>APPLICATION FORM</h2>
            <p className={styles.sessionInfo}>
              Academic Session: {user?.sessionId}
            </p>
          </div>
        </div>
        <div className={styles.applicationNumber}>
          Application No: <strong>{personalInfo?.applicationNumber}</strong>
        </div>
      </div>

      {/* Program Information */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3>PROGRAMME INFORMATION</h3>
        </div>
        <div className={styles.sectionContent}>
          <div className={styles.grid}>
            <div className={styles.fieldGroup}>
              <label>Programme:</label>
              <span>{user?.programName}</span>
            </div>
            <div className={styles.fieldGroup}>
              <label>Degree Type:</label>
              <span>{user?.programType}</span>
            </div>
            <div className={styles.fieldGroup}>
              <label>Mode of Study:</label>
              <span>{user?.modeOfStudyName}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Personal Information */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3>SECTION 1: PERSONAL INFORMATION</h3>
        </div>
        <div className={styles.sectionContent}>
          <div className={styles.personalInfoLayout}>
            <div className={styles.photoSection}>
              <div className={styles.passportPhoto}>
                <img
                  src={personalInfo?.imageUrl}
                  alt="Applicant"
                  className={styles.photo}
                />
              </div>
              <p className={styles.photoLabel}>Passport Photograph</p>
            </div>
            <div className={styles.personalDetails}>
              <div className={styles.gridThree}>
                <div className={styles.fieldGroup}>
                  <label>First Name:</label>
                  <span>{personalInfo?.fName}</span>
                </div>
                <div className={styles.fieldGroup}>
                  <label>Middle Name:</label>
                  <span>{personalInfo?.mNane}</span>
                </div>
                <div className={styles.fieldGroup}>
                  <label>Last Name:</label>
                  <span>{personalInfo?.lName}</span>
                </div>
              </div>
              <div className={styles.gridTwo}>
                <div className={styles.fieldGroup}>
                  <label>Gender:</label>
                  <span>{personalInfo?.gender}</span>
                </div>
                <div className={styles.fieldGroup}>
                  <label>Date of Birth:</label>
                  <span>{formatFullDate(personalInfo?.dateOfBirth)}</span>
                </div>
              </div>
              <div className={styles.gridTwo}>
                <div className={styles.fieldGroup}>
                  <label>Phone Number:</label>
                  <span>{personalInfo?.phoneNo}</span>
                </div>
                <div className={styles.fieldGroup}>
                  <label>Email Address:</label>
                  <span>{personalInfo?.email}</span>
                </div>
              </div>
              <div className={styles.fieldGroup}>
                <label>Residential Address:</label>
                <span>{personalInfo?.address}</span>
              </div>
              <div className={styles.fieldGroup}>
                <label>Country:</label>
                <span>{personalInfo?.countryName}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Academic Qualifications */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3>SECTION 2: ACADEMIC QUALIFICATIONS</h3>
        </div>
        <div className={styles.sectionContent}>
          {shouldShowInstitutionSection && (
            <>
              <h4 className={styles.subsectionTitle}>
                A. Post-Secondary Education
              </h4>
              {institutions?.map((institution) => (
                <div key={institution.id} className={styles.institutionBlock}>
                  <div className={styles.gridTwo}>
                    <div className={styles.fieldGroup}>
                      <label>Institution:</label>
                      <span>{institution.institutionName}</span>
                    </div>
                    <div className={styles.fieldGroup}>
                      <label>Discipline/Major:</label>
                      <span>{institution.discipline}</span>
                    </div>
                  </div>
                  <div className={styles.gridThree}>
                    <div className={styles.fieldGroup}>
                      <label>Start Date:</label>
                      <span>{formatFullDate(institution.startDate)}</span>
                    </div>
                    <div className={styles.fieldGroup}>
                      <label>End Date:</label>
                      <span>{formatFullDate(institution.endDate)}</span>
                    </div>
                    <div className={styles.fieldGroup}>
                      <label>CGPA:</label>
                      <span>{institution.cgpa}/5.0</span>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {qualifications && qualifications.length > 0 && (
            <h4 className={styles.subsectionTitle}>
              {shouldShowInstitutionSection
                ? "B. O'Level Qualifications"
                : "A. O'Level Qualifications"}
            </h4>
          )}
          {qualifications?.map((qualification) => (
            <div key={qualification.id} className={styles.qualificationBlock}>
              <div className={styles.gridTwo}>
                <div className={styles.fieldGroup}>
                  <label>Qualification Type:</label>
                  <span>{qualification.qualificationType}</span>
                </div>
                <div className={styles.fieldGroup}>
                  <label>Examination Number:</label>
                  <span>{qualification.examNumber}</span>
                </div>
              </div>
              <div className={styles.resultTable}>
                <table>
                  <thead>
                    <tr>
                      <th>S/N</th>
                      <th>Subject</th>
                      <th>Grade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {qualification.subjects.map((subject, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td>{subject.subject}</td>
                        <td>{subject.grade}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {shouldShowUtmeSection && (
            <>
              <h4 className={styles.subsectionTitle}>
                {shouldShowInstitutionSection
                  ? "C. UTME Result"
                  : "B. UTME Result"}
              </h4>
              <div className={styles.qualificationBlock}>
                <div className={styles.gridThree}>
                  <div className={styles.fieldGroup}>
                    <label>Exam Number:</label>
                    <span>{jambResult.examNumber}</span>
                  </div>
                  <div className={styles.fieldGroup}>
                    <label>Exam Year:</label>
                    <span>{jambResult.examYear}</span>
                  </div>
                  <div className={styles.fieldGroup}>
                    <label>Total Score:</label>
                    <span>{jambResult.totalScore}</span>
                  </div>
                </div>
                <div className={styles.resultTable}>
                  <table>
                    <thead>
                      <tr>
                        <th>S/N</th>
                        <th>Subject</th>
                        <th>Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jambResult.subjects?.map((subject, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{subject.subjectName}</td>
                          <td>{subject.score}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Work Experience */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3>SECTION 3: WORK EXPERIENCE</h3>
        </div>
        <div className={styles.sectionContent}>
          {workHistory?.map((work) => (
            <div key={work.id} className={styles.workBlock}>
              <div className={styles.gridTwo}>
                <div className={styles.fieldGroup}>
                  <label>Company/Organization:</label>
                  <span>{work.companyName}</span>
                </div>
                <div className={styles.fieldGroup}>
                  <label>Position/Role:</label>
                  <span>{work.position}</span>
                </div>
              </div>
              <div className={styles.fieldGroup}>
                <label>Duration:</label>
                <span>
                  {formatFullDate(work.startDate)} -{" "}
                  {isValidDate(work.endDate)
                    ? formatFullDate(work.endDate)
                    : "Present"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Documents Uploaded (as list) */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3>SECTION 4: DOCUMENTS UPLOADED</h3>
        </div>
        <div className={styles.sectionContent}>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>
              <strong>NYSC Document:</strong>{" "}
              <a
                href={nyscUpload?.nyscUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {documentName(nyscUpload?.nyscUrl)}
              </a>
            </li>

            {qualifications?.map((q) => (
              <li key={q.id}>
                <strong>{q.qualificationType} Certificate:</strong>{" "}
                <a
                  href={(q as any)?.certificateiUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {documentName((q as any)?.certificateiUrl)}
                </a>
              </li>
            ))}

            {workHistory?.map((w) => (
              <li key={w.id}>
                <strong>Employment Document ({w.companyName}):</strong>{" "}
                <a
                  href={(w as any)?.employmentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {documentName((w as any)?.employmentUrl)}
                </a>
              </li>
            ))}

            {shouldShowUtmeSection &&
              (jambResult?.jambResultUrl || jambResult?.jambResult) && (
              <li>
                <strong>JAMB Result:</strong>{" "}
                <a
                  href={jambResult?.jambResultUrl || jambResult?.jambResult}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {documentName(
                    jambResult?.jambResultUrl || jambResult?.jambResult,
                  )}
                </a>
              </li>
            )}
          </ul>
        </div>
      </div>

      {/* Payment Summary */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3>SECTION 5: PAYMENT SUMMARY</h3>
        </div>
        <div className={styles.sectionContent}>
          <div className={styles.paymentTable}>
            <table>
              <tbody>
                <tr>
                  <td>Application Fee:</td>
                  <td className={styles.amount}>
                    {formatCurrency(user?.earlyApplicationAmount ?? 0)}
                  </td>
                </tr>
                {shouldPayLateApplicationFee && (
                  <tr>
                    <td>Late Application Fee:</td>
                    <td className={styles.amount}>
                      {formatCurrency(lateApplicationFee)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationForm;
